/**
 * feat-300 — the thread's message log (design NOTES §3.3, §3.6, §3.7).
 *
 * UNTRUSTED TEXT: reviewer turns, the AI's clarifying questions and proposal
 * summaries are all rendered as JSX text nodes. Nothing here uses
 * `dangerouslySetInnerHTML` (ADR §3.3).
 */
import React, { useState } from 'react';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Keyboard,
  MessageCircleQuestion,
  Mic,
  RotateCcw,
  Sparkles,
} from 'lucide-react';
import { QuizQuestion } from '../../../../services/quizContentAdminService';
import {
  QuestionEditMessage,
  QuestionEditThread,
} from '../../../../services/questionEditService';
import { WorkingSpinner } from './MicButton';
import ProposalCard, { ProposalActions } from './ProposalCard';
import {
  BOX_FAILED,
  BTN_QUIET,
  BTN_SECONDARY,
  HINT,
  HIST_BTN,
  META,
  MSG_AI,
  MSG_YOU,
  PROPOSAL,
  PROPOSAL_HEAD,
  SUMMARY,
} from './classes';
import { metaWhen } from './formatWhen';
import { failureSentence, QUESTION_EDIT_COPY as COPY } from './voiceEditCopy';

export function whoLabel(email: string | null | undefined, currentEmail: string | null): string {
  if (!email) return '';
  return currentEmail && email.toLowerCase() === currentEmail.toLowerCase() ? COPY.you : email;
}

const ReviewerMessage: React.FC<{ m: QuestionEditMessage; currentEmail: string | null }> = ({
  m,
  currentEmail,
}) => (
  <div data-testid="qe-msg" data-author="reviewer">
    <div className={META}>
      {/* Security M3: Replay runs with maskAllText:false — every email and every
          piece of thread text carries data-sentry-mask (TrackConflictBanner precedent). */}
      <span data-sentry-mask className="text-ink" data-testid="qe-msg-who">
        {whoLabel(m.author_email, currentEmail)}
      </span>
      {m.input_mode === 'voice' ? (
        <span className="inline-flex items-center gap-1">
          · <Mic className="w-3 h-3" aria-hidden />
          {COPY.byVoice}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1">
          · <Keyboard className="w-3 h-3" aria-hidden />
          {COPY.typed}
        </span>
      )}
      <span>· {metaWhen(m.created_at)}</span>
    </div>
    <p data-sentry-mask className={MSG_YOU} data-testid="qe-msg-text">
      {m.text ?? ''}
    </p>
  </div>
);

const AiMeta: React.FC<{ at: string }> = ({ at }) => (
  <div className={META}>
    <Sparkles className="w-3 h-3" aria-hidden />
    <span className="text-ink">{COPY.ai}</span>
    <span>· {metaWhen(at)}</span>
  </div>
);

/** An earlier proposal: one line, expandable to its summary (read-only). The
 *  contract keeps only the CURRENT proposal's fields, so a replaced one shows
 *  its summary and no field count (QUESTIONS-FRONTEND F6). */
const ReplacedProposal: React.FC<{ m: QuestionEditMessage; summary: string }> = ({ m, summary }) => {
  const [open, setOpen] = useState(false);
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div data-testid="qe-msg" data-author="ai">
      <AiMeta at={m.created_at} />
      <button
        type="button"
        className={`${HIST_BTN} mt-1`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Chevron className="w-3 h-3" aria-hidden />
        <span className="line-through decoration-inkMuted/60">{COPY.replacedProposalNoCount}</span>
        {COPY.replacedSuffix}
      </button>
      {open && (
        <div className={`${PROPOSAL} mt-1`}>
          <div className={PROPOSAL_HEAD}>
            <p data-sentry-mask className={SUMMARY}>
              {summary}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export interface ThreadLogProps {
  thread: QuestionEditThread;
  q: QuizQuestion | null;
  currentEmail: string | null;
  /** History: no actions, no working row, no failed box. */
  readOnly?: boolean;
  /** Live-panel actions; absent in read-only mode. */
  actions?: ProposalActions & {
    onRetry: () => void;
    onDiscard: () => void;
    busy: boolean;
  };
  approvalRequired: boolean | null;
}

const ThreadLog: React.FC<ThreadLogProps> = ({
  thread,
  q,
  currentEmail,
  readOnly = false,
  actions,
  approvalRequired,
}) => {
  const messages = thread.messages ?? [];
  let lastProposalSeq = -1;
  messages.forEach((m) => {
    if (m.author === 'ai' && m.ai?.kind === 'proposal') lastProposalSeq = m.seq;
  });

  return (
    <>
      {messages.map((m) => {
        if (m.author === 'reviewer') {
          return <ReviewerMessage key={`s${m.seq}`} m={m} currentEmail={currentEmail} />;
        }
        if (!m.ai) return null;
        if (m.ai.kind === 'clarify') {
          return (
            <div key={`s${m.seq}`} data-testid="qe-msg" data-author="ai">
              <AiMeta at={m.created_at} />
              <p className={`${MSG_AI} flex items-start gap-1.5`}>
                <MessageCircleQuestion className="w-4 h-4 text-ink shrink-0 mt-0.5" aria-hidden />
                <span data-sentry-mask data-testid="qe-msg-clarify">
                  <span className="sr-only">{COPY.aiQuestionSr}</span>
                  {m.ai.question}
                </span>
              </p>
            </div>
          );
        }
        // A proposal turn.
        if (m.seq !== lastProposalSeq) {
          return <ReplacedProposal key={`s${m.seq}`} m={m} summary={m.ai.summary} />;
        }
        const live =
          !readOnly && thread.state === 'proposal_ready' && thread.proposal && actions;
        return (
          <div key={`s${m.seq}`} data-testid="qe-msg" data-author="ai">
            <AiMeta at={m.created_at} />
            <div className="mt-1">
              {live ? (
                <ProposalCard
                  thread={thread}
                  proposal={thread.proposal!}
                  q={q}
                  approvalRequired={approvalRequired}
                  actions={actions!}
                />
              ) : (
                <div className={PROPOSAL}>
                  <div className={PROPOSAL_HEAD}>
                    <p data-sentry-mask className={SUMMARY}>
                      {m.ai.summary}
                    </p>
                  </div>
                  {readOnly && thread.state === 'applied' && (
                    <div
                      data-sentry-mask
                      className={`${HINT} flex items-center gap-1.5 border-t border-softBorder px-3 py-2.5`}
                    >
                      <Check className="w-3.5 h-3.5 text-success-on" aria-hidden />
                      {COPY.appliedBy(
                        whoLabel(thread.closed_by_email, currentEmail),
                        thread.closed_at ? metaWhen(thread.closed_at) : '',
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {!readOnly && thread.state === 'working' && (
        <div className="flex items-start gap-2 text-sm text-inkMuted" aria-busy="true" data-testid="qe-working">
          <span className="mt-0.5">
            <WorkingSpinner />
          </span>
          <p>
            <span className="font-semibold text-ink">{COPY.working}</span> {COPY.workingLeave}
          </p>
        </div>
      )}

      {!readOnly && thread.state === 'failed' && actions && (
        <div className={BOX_FAILED} role="alert" data-testid="qe-failed">
          <p className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
            <span>
              <strong className="font-semibold">{COPY.failedLead}</strong>{' '}
              {failureSentence(thread.failure_reason)}
            </span>
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={BTN_SECONDARY}
              onClick={actions.onRetry}
              disabled={actions.busy}
            >
              <RotateCcw className="w-3.5 h-3.5" aria-hidden />
              {COPY.retry}
            </button>
            <button
              type="button"
              className={`${BTN_QUIET} !text-danger-text hover:!bg-white/60`}
              onClick={actions.onDiscard}
              disabled={actions.busy}
            >
              {COPY.discard}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ThreadLog;
